import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../utils/supabase';

const ProfileTab = ({ onMessage }) => {
  const navigate = useNavigate();
  const { user, userProfile, updateProfile } = useAuth();
  
  // Account deletion state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Personal information state
  const [personalInfo, setPersonalInfo] = useState({
    full_name: ''
  });
  const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Load user profile data
  useEffect(() => {
    if (userProfile) {
      setPersonalInfo({
        full_name: userProfile.full_name || ''
      });
    }
  }, [userProfile]);

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText !== 'DELETE MY ACCOUNT') {
      onMessage('error', 'Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setDeletingAccount(true);

    try {
      console.log('Starting secure account deletion process...');
      
      // Call the secure Edge Function for complete deletion
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          userId: user.id,
          confirmationText: deleteConfirmationText
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to process deletion request');
      }

      if (!data.success) {
        throw new Error(data.error || 'Deletion failed');
      }

      console.log('Deletion successful:', data);
      
      // Show detailed success message
      const { deletedData } = data;
      onMessage('success', 
        `Account successfully deleted! ` +
        `Profile: ${deletedData.profileDeleted ? 'Removed' : 'Not found'}, ` +
        `Memberships: ${deletedData.membershipsDeleted} removed, ` +
        `Organizations affected: ${deletedData.organizationsAffected}. ` +
        `You will be redirected to login...`
      );

      // Sign out the user (auth user is already deleted by Edge Function)
      await supabase.auth.signOut();

      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      console.error('Account deletion error:', error);
      onMessage('error', 
        error.message || 'Failed to delete account. Please try again or contact support.'
      );
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeleteConfirmationText('');
  };

  // Personal information handlers
  const handlePersonalInfoChange = (key, value) => {
    setPersonalInfo(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const savePersonalInfo = async () => {
    setSavingPersonalInfo(true);
    
    // Debug logging
    console.log('Saving personal info:', personalInfo);
    console.log('User ID:', user?.id);
    
    try {
      const result = await updateProfile(personalInfo);
      
      console.log('Update result:', result);
      
      if (result?.success) {
        onMessage('success', 'Personal information updated successfully!');
      } else {
        console.error('Update failed:', result?.error);
        onMessage('error', result?.error || 'Failed to update personal information');
      }
    } catch (error) {
      console.error('Error updating personal info:', error);
      onMessage('error', 'An unexpected error occurred while updating your information');
    } finally {
      setSavingPersonalInfo(false);
    }
  };

  // Password change handlers
  const handlePasswordChange = (key, value) => {
    setPasswordData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const changePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      onMessage('error', 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      onMessage('error', 'New password must be at least 6 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        throw error;
      }

      onMessage('success', 'Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      onMessage('error', error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };





  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Personal Information
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Full Name
            </label>
            <Input
              type="text"
              value={personalInfo.full_name}
              onChange={(e) => handlePersonalInfoChange('full_name', e.target.value)}
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Email Address
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600">{user?.email}</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Your account email address cannot be changed
            </p>
          </div>





          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              onClick={savePersonalInfo}
              disabled={savingPersonalInfo || !personalInfo.full_name.trim()}
              variant="primary"
            >
              {savingPersonalInfo ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving Personal Info...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Save Personal Info
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Security
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <Input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              placeholder="Enter new password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <Input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              placeholder="Confirm new password"
            />
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              onClick={changePassword}
              disabled={changingPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              variant="secondary"
            >
              {changingPassword ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating Password...
                </>
              ) : (
                <>
                  <Icon name="Shield" size={16} className="mr-2" />
                  Update Password
                </>
              )}
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Password Requirements</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Minimum 6 characters</li>
                  <li>• Recommended: Include numbers and special characters</li>
                  <li>• Avoid common passwords</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Account Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Account Information
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              User ID
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600 font-mono">{user?.id}</span>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              Your unique user identifier
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Account Created
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              <span className="text-sm text-gray-600">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-900 mb-4">
          Danger Zone
        </h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-red-800 mb-2">Delete Account</h4>
            <p className="text-sm text-red-700 mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            
            <div className="bg-red-100 border border-red-300 rounded-md p-4 mb-4">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-red-600 mr-3 mt-0.5" />
                <div>
                  <h5 className="font-medium text-red-800 mb-2">This will permanently delete:</h5>
                  <ul className="text-sm text-red-700 space-y-1 mb-3">
                    <li>• Your user account and authentication credentials</li>
                    <li>• Your user profile and personal settings</li>
                    <li>• All organization memberships</li>
                    <li>• Your email logs and activity history</li>
                  </ul>
                  
                  <h5 className="font-medium text-red-800 mb-2">What will be preserved:</h5>
                  <ul className="text-sm text-red-700 space-y-1 mb-3">
                    <li>• Organization data (for other members)</li>
                    <li>• Customer records (anonymized)</li>
                    <li>• Review data (anonymized)</li>
                    <li>• Business analytics (anonymized)</li>
                  </ul>
                  
                  <div className="bg-red-200 border border-red-400 rounded p-2 text-xs text-red-900">
                    <strong>⚠️ Important:</strong> Organizations with no remaining members will be marked as inactive. This action cannot be reversed.
                  </div>
                </div>
              </div>
            </div>

            {!showDeleteConfirmation ? (
              <Button
                onClick={() => setShowDeleteConfirmation(true)}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Icon name="Trash2" size={16} className="mr-2" />
                Delete Account
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-red-800 mb-2">
                    Type "DELETE MY ACCOUNT" to confirm:
                  </label>
                  <Input
                    type="text"
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className="border-red-300 focus:border-red-500 focus:ring-red-500"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount || deleteConfirmationText !== 'DELETE MY ACCOUNT'}
                    className="bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300"
                  >
                    {deletingAccount ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Icon name="Trash2" size={16} className="mr-2" />
                        Delete Account
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleCancelDelete}
                    variant="outline"
                    disabled={deletingAccount}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab; 